import apiClient from './client';
import type {
  Lead,
  CreateLeadRequest,
  LeadFilters,
  BulkUpdateLeadsRequest,
  PaginatedRequest,
  PaginatedResponse,
  AddressCheckResponse,
} from '../types';

/**
 * Check if address is in a service area (public)
 */
export async function checkAddress(
  address: string,
  latitude: number,
  longitude: number
): Promise<AddressCheckResponse> {
  const response = await apiClient.post<AddressCheckResponse>('/public/check-address', {
    address,
    latitude,
    longitude,
  });
  return response.data;
}

/**
 * Create a new lead (public)
 */
export async function createLead(data: CreateLeadRequest): Promise<Lead> {
  const response = await apiClient.post<Lead>('/public/leads', data);
  return response.data;
}

/**
 * Admin request to create a lead
 */
export interface AdminCreateLeadRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  fullAddress: string;
  serviceAreaId?: string;
  preferredServiceTierId?: string;
  consentToContact: boolean;
  source?: string;
  referralCode?: string;
  notes?: string;
  // Structured address components from Mapbox autocomplete. When provided
  // the backend skips its comma-split fallback parser and writes these
  // directly to the Address row's per-field columns.
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Create a new lead (admin)
 */
export async function createLeadAdmin(data: AdminCreateLeadRequest): Promise<Lead> {
  const response = await apiClient.post<Lead>('/leads', data);
  return response.data;
}

/**
 * Get paginated leads with filters (admin)
 */
export async function getLeads(
  params: PaginatedRequest & LeadFilters
): Promise<PaginatedResponse<Lead>> {
  const response = await apiClient.get<PaginatedResponse<Lead>>('/leads', { params });
  return response.data;
}

/**
 * Get single lead (admin)
 */
export async function getLead(id: string): Promise<Lead> {
  const response = await apiClient.get<Lead>(`/leads/${id}`);
  return response.data;
}

/**
 * Update lead status (admin)
 */
export async function updateLeadStatus(id: string, status: string): Promise<Lead> {
  const response = await apiClient.put<Lead>(`/leads/${id}`, { status });
  return response.data;
}

/**
 * Update lead (admin)
 */
export interface UpdateLeadRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  fullAddress?: string;
  latitude?: number;
  longitude?: number;
  preferredServiceTierId?: string;
  status?: string;
  consentToContact?: boolean;
  source?: string;
  notes?: string;
  serviceAreaId?: string;
  addressId?: string;
  notSchedulableBefore?: string | null;
}

export async function updateLead(id: string, data: UpdateLeadRequest): Promise<Lead> {
  const response = await apiClient.put<Lead>(`/leads/${id}`, data);
  return response.data;
}

/**
 * Bulk update lead statuses (admin)
 */
export async function bulkUpdateLeads(data: BulkUpdateLeadsRequest): Promise<void> {
  await apiClient.post('/leads/bulk-update', data);
}

/**
 * Export leads to file (admin)
 */
export async function exportLeads(
  filters: LeadFilters,
  format: 'CSV' | 'Excel'
): Promise<Blob> {
  const response = await apiClient.post(
    '/leads/export',
    { filters, format },
    { responseType: 'blob' }
  );
  return response.data;
}

/**
 * Delete lead (admin)
 */
export async function deleteLead(id: string): Promise<void> {
  await apiClient.delete(`/leads/${id}`);
}

/**
 * Lead statistics
 */
export interface LeadStats {
  totalLeads: number;
  newCount: number;
  contactedCount: number;
  qualifiedCount: number;
  convertedCount: number;
  notInterestedCount: number;
  unresponsiveCount: number;
  invalidCount: number;
  newThisWeek: number;
  newThisMonth: number;
  convertedThisMonth: number;
  conversionRate: number;
  averageDaysToConversion: number | null;
  averageDaysToContact: number | null;
}

/**
 * Get lead statistics (admin)
 */
export async function getLeadStats(): Promise<LeadStats> {
  const response = await apiClient.get<LeadStats>('/leads/stats');
  return response.data;
}
